import { amendStage, beginStage, clearStage, currentStage, isJourneyStage, journeyToken, DEFERRED_CANCEL_COOKIE, JourneyStages } from '@cognitoforms/api/services/journey-context';
import { buildCancelSignal, buildOrphanStepSignal, buildStartSignal, buildStepSignal, dimensionName, isReservedDimension } from './envelope';
import { experimentFlags } from './experiment-flags';
import { resolveUpgradeStart } from './registry';
import { trackClickEvent } from '../telemetry';
import { CognitoConfiguration } from '@cognitoforms/utils/clientside-configuration';
import session from '@cognitoforms/api/services/session';
import type { ClickAnalyticsLike } from '../telemetry';
import type { JourneySignal, JourneySignalContext, StageDimensions } from './envelope';
import type { ActiveStage, JourneyStage, StageStart } from '@cognitoforms/api/services/journey-context';

export const JOURNEY_SELECTOR = '[data-cf-start],[data-cf-step]';

const START_ATTRIBUTE = 'data-cf-start';
const STEP_ATTRIBUTE = 'data-cf-step';
const STAGE_ATTRIBUTE = 'data-cf-stage';
const VARIANT_ATTRIBUTE = 'data-cf-variant';
const ENTRY_POINT_ATTRIBUTE = 'data-cf-entry-point';
const DETAIL_ATTRIBUTE = 'data-cf-detail';
const COMPLETED_ATTRIBUTE = 'data-cf-completed';

/**
 * The attribute that carries a form's id on any element the journey listens to. It is also the `FormId`
 * identity dimension on every event emitted from such an element.
 */
export const FORM_ID_ATTRIBUTE = 'data-cf-form-id';

type StageSubject = {
	/** The attribute on a Start element that carries the subject's id. */
	attribute: string;
};

/**
 * The stages that take their correlation id from their subject rather than from a declaration travelling
 * between their two halves, and where the browser reads that subject's id. Must name the same stages as
 * `JourneyContext.IsSubjectKeyed` on the server.
 */
const stageSubjects: Partial<Record<JourneyStage, StageSubject>> = {
	ActivateForm: { attribute: FORM_ID_ATTRIBUTE }
};

export function stageSubject(stage: JourneyStage): StageSubject | undefined {
	return stageSubjects[stage];
}

/**
 * Arriving at the organization-creation page without having just signed up — a user whose only organization
 * was deleted, or who was removed from it. A signup reaches the same page with the same zero memberships,
 * and is told apart by still holding the stage it opened back on `/signup`.
 */
const orphanedUserStart: StageStart = { stage: JourneyStages.CreateOrganization, variant: 'OrphanedUser' };

/**
 * The attributes that say what an element is rather than what it carries: which role it plays and which
 * stage it names. Every other `data-cf-` attribute on an element the journey emits for becomes a dimension
 * named after it, including the ones a Start is also described by.
 */
const STRUCTURAL_ATTRIBUTES: readonly string[] = [
	START_ATTRIBUTE,
	STEP_ATTRIBUTE,
	STAGE_ATTRIBUTE,
	'data-cf-id',
	'data-cf-element-id'
];

const MAX_CANCEL_REASON_LENGTH = 64;

let lastEmittedTag: string | null = null;
let installed = false;
let pluginProvider: (() => ClickAnalyticsLike | null) | null = null;

function warn(message: string, error: unknown): void {
	if (CognitoConfiguration.IsProduction === false)
		console.warn(message, error);
}

/**
 * Whether the journey listener already emitted the event for this tag, which is how the initializer drops
 * the Click Analytics plugin's duplicate copy of the same activation.
 */
export function wasEmittedByJourney(tag: string): boolean {
	return lastEmittedTag === tag;
}

export function resetJourneyEmission(): void {
	lastEmittedTag = null;
}

function signalContext(stage: JourneyStage, element?: Element): JourneySignalContext {
	const context: JourneySignalContext = {};

	const visitorId = CognitoConfiguration.ReferrerId?.trim();
	if (visitorId)
		context.visitorId = visitorId;

	const info = session.value;
	if (info) {
		context.organizationId = info.organizationId ?? undefined;
		context.userId = info.userId ?? undefined;
	}

	const formId = element?.getAttribute(FORM_ID_ATTRIBUTE)?.trim();
	if (formId)
		context.formId = formId;

	context.experimentFlags = experimentFlags(stage);

	return context;
}

function collectDataAttributes(element: Element): Record<string, string> {
	const attrs: Record<string, string> = {};
	for (const attr of Array.from(element.attributes)) {
		if (attr.name.startsWith('data-cf-'))
			attrs[attr.name] = attr.value;
	}
	return attrs;
}

function emitSignal(plugin: ClickAnalyticsLike | null, attrs: Record<string, string>, signal: JourneySignal, tag: string): void {
	const elementId = attrs['data-cf-id'];
	if (elementId && elementId !== signal.name)
		attrs['data-cf-element-id'] = elementId;
	attrs['data-cf-id'] = signal.name;

	lastEmittedTag = tag;
	trackClickEvent(plugin, attrs, signal.dimensions);
}

/**
 * The dimensions an element names for itself. Every `data-cf-` attribute the journey does not read
 * structurally is emitted under the name it asks for, so a Start or a Step carries whatever the surface
 * knows about it without either side declaring the dimension anywhere.
 */
function declaredDimensions(attrs: Record<string, string>): Record<string, string> {
	const named: Record<string, string> = {};

	for (const [attribute, value] of Object.entries(attrs)) {
		if (STRUCTURAL_ATTRIBUTES.includes(attribute))
			continue;

		const name = dimensionName(attribute);
		if (name)
			named[name] = value;
	}

	return sanitizeDimensions(named);
}

/**
 * The dimensions as they can be emitted: named values put through {@link journeyToken}, with anything empty
 * or named after a key the plugin writes for itself dropped.
 */
function sanitizeDimensions(named: Record<string, string>): Record<string, string> {
	const dimensions: Record<string, string> = {};

	for (const [name, value] of Object.entries(named)) {
		if (!value || isReservedDimension(name))
			continue;

		const token = journeyToken(value);
		if (token)
			dimensions[name] = token;
	}

	return dimensions;
}

function emit(plugin: ClickAnalyticsLike | null, element: Element, signal: JourneySignal, tag: string): void {
	const attrs = collectDataAttributes(element);
	emitSignal(plugin, attrs, { ...signal, dimensions: { ...declaredDimensions(attrs), ...signal.dimensions } }, tag);
}

/**
 * Opens a stage from code, for a Start that is an arrival rather than a click. The stage is opened before
 * the event is emitted and independently of it, so a blocked telemetry plugin still leaves the correlation
 * id riding the request header. `attrs` stands in for the `data-cf-` attributes the element would have
 * carried, so a Start moved into code keeps the click id and the dimensions its markup named.
 */
export function startStage(start: StageStart, attrs: Record<string, string> = {}): ActiveStage | null {
	try {
		const active = beginStage(start);
		const signal = buildStartSignal(active, signalContext(active.stage));
		emitSignal(
			pluginProvider ? pluginProvider() : null,
			{ ...attrs },
			{ ...signal, dimensions: { ...declaredDimensions(attrs), ...signal.dimensions } },
			`start:${start.stage}`);
		return active;
	}
	catch (e) {
		warn('journey start failed:', e);
		return null;
	}
}

/**
 * What a step says about itself, beyond its name. `variant`, `entryPoint` and `detail` are also written back
 * onto the open stage where it has not got them already, so a value the Start could not know rides
 * `X-CF-Stage` from here on and reaches the stage's Complete.
 */
export type StepOptions = {
	/** The stage the step belongs to. Omit for the most recently opened one. */
	stage?: JourneyStage;
	formId?: string;
	variant?: string;
	entryPoint?: string;
	detail?: string;
	/** Anything else the step wants carried, under the dimension names given. */
	dimensions?: Record<string, string>;
};

/**
 * The three standard slots a step declares, bounded the same way any other emitted value is — so a stage
 * amended by a step holds exactly what the step's own signal carries.
 */
function stageDimensions(options: StepOptions): StageDimensions {
	return {
		variant: journeyToken(options.variant ?? '') || undefined,
		entryPoint: journeyToken(options.entryPoint ?? '') || undefined,
		detail: journeyToken(options.detail ?? '') || undefined
	};
}

/**
 * Records a step from code, for something that happens inside a stage without a click to hang
 * `data-cf-step` on. A step reached with no stage open is emitted without a correlation id where it names
 * its own stage, and dropped where it does not.
 */
export function recordStep(step: string, options: StepOptions = {}): void {
	try {
		if (!step)
			return;

		const declared = stageDimensions(options);

		let active = currentStage(options.stage);
		if (active && (declared.variant || declared.entryPoint || declared.detail))
			active = amendStage(active.stage, declared) ?? active;

		const stage = active?.stage ?? options.stage;
		if (!stage)
			return;

		const context = signalContext(stage);
		if (options.formId)
			context.formId = options.formId;

		const signal = active
			? buildStepSignal(step, active, context)
			: options.stage ? buildOrphanStepSignal(step, options.stage, context, declared) : null;

		if (!signal)
			return;

		emitSignal(
			pluginProvider ? pluginProvider() : null,
			{},
			{ ...signal, dimensions: { ...sanitizeDimensions(options.dimensions ?? {}), ...signal.dimensions } },
			`step:${step}`);
	}
	catch (e) {
		warn('journey step failed:', e);
	}
}

/**
 * Opens a stage for a surface that can be reached either cold or from inside an attempt it already belongs to,
 * such as a template card — which both begins a creation and narrows one someone began by browsing. An attempt
 * already open under the same variant is stepped rather than reopened, so it keeps the one Start it began with
 * and stays attributed to where it began; anything else, including an attempt at a different variant, opens a
 * new stage as usual. `attrs` is forwarded to the Start as {@link startStage} takes it.
 */
export function startOrStep(start: StageStart, step: string, dimensions?: Record<string, string>, attrs?: Record<string, string>): void {
	const open = currentStage(start.stage);
	if (open && open.variant === start.variant)
		recordStep(step, { stage: start.stage, detail: start.detail, dimensions });
	else
		startStage(start, attrs);
}

/**
 * Opens the paid-conversion stage for a surface that is showing someone the plans, and does nothing where
 * one is already open — {@link startStage} would otherwise mint a second correlation id and orphan the
 * intent already recorded against the first. `attrs` is forwarded to the Start as {@link startStage} takes it.
 */
export function startUpgradeStage(source?: string, details?: string, isSignUp = false, attrs?: Record<string, string>): ActiveStage | null {
	if (currentStage(JourneyStages.ConvertToPaid))
		return null;

	return startStage(resolveUpgradeStart(source, details, isSignUp), attrs);
}

/**
 * Opens the organization-creation stage for a user who reached the create-organization page without signing
 * up, and does nothing for one who signed up — an already-open stage is what tells the two apart, and
 * starting here would replace a live signup with a correlation id its Complete would never match.
 */
export function startOrphanedUserStage(): ActiveStage | null {
	if (currentStage(JourneyStages.CreateOrganization))
		return null;

	return startStage(orphanedUserStart);
}

function takeCookie(name: string): string | null {
	const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`));
	if (!match)
		return null;

	document.cookie = `${name}=;path=/;expires=Thu, 01 Jan 1970 00:00:00 GMT`;
	return decodeURIComponent(match[1]);
}

/**
 * Emits the cancellation the server left on {@link DEFERRED_CANCEL_COOKIE} because the navigation it found
 * the contradiction on had no response a script would read, and closes the stage it retracts. The cookie is
 * consumed whether or not it turns out to be usable.
 */
export function applyDeferredCancellation(): void {
	try {
		const value = takeCookie(DEFERRED_CANCEL_COOKIE);
		if (!value)
			return;

		const separator = value.indexOf(':');
		if (separator < 1)
			return;

		const stage = value.slice(0, separator);
		const reason = value.slice(separator + 1);
		if (!isJourneyStage(stage) || !isCancelReason(reason))
			return;

		const active = currentStage(stage);
		if (!active)
			return;

		clearStage(stage);
		emitSignal(pluginProvider ? pluginProvider() : null, {}, buildCancelSignal(active, reason, signalContext(stage)), `cancel:${stage}`);
	}
	catch (e) {
		warn('journey cancellation failed:', e);
	}
}

/**
 * Whether the reason on the cookie is one we could have written — a bare token of the same alphabet every
 * other journey dimension uses.
 */
function isCancelReason(value: string): boolean {
	return value.length > 0 && journeyToken(value, MAX_CANCEL_REASON_LENGTH) === value;
}

/**
 * The stage a step names, from the element or the nearest ancestor that names one, so a shared component can
 * name it once for everything it wraps. A value that is not a stage is ignored rather than searched past,
 * falling through to the most recently started stage.
 */
function requestedStage(element: Element): JourneyStage | undefined {
	const stage = element.closest(`[${STAGE_ATTRIBUTE}]`)?.getAttribute(STAGE_ATTRIBUTE);
	return stage && isJourneyStage(stage) ? stage : undefined;
}

/**
 * What an element says about its Start, taken from itself or from the nearest ancestor that says it, so a
 * wrapper can describe every Start inside it once.
 */
function inherited(element: Element, attribute: string): string | undefined {
	const value = element.closest(`[${attribute}]`)?.getAttribute(attribute);
	return value ? journeyToken(value) || undefined : undefined;
}

/**
 * What the element itself says, not counting what a wrapper says about the Starts inside it. A step that
 * amends the stage it is inside has to be the thing that said so.
 */
function declared(element: Element, attribute: string): string | undefined {
	const value = element.getAttribute(attribute);
	return value ? journeyToken(value) || undefined : undefined;
}

/**
 * The Start an element declares: `data-cf-start` names the stage, and `data-cf-variant`,
 * `data-cf-entry-point` and `data-cf-detail` say which flavour of it this is, where it was opened from, and
 * whatever else the surface wanted carried. A `data-cf-start` that does not name a stage opens nothing.
 */
function declaredStart(element: Element): StageStart | null {
	const stage = element.getAttribute(START_ATTRIBUTE)?.trim();
	if (!stage || !isJourneyStage(stage))
		return null;

	const start: StageStart = { stage, variant: inherited(element, VARIANT_ATTRIBUTE) ?? '' };

	const entryPoint = inherited(element, ENTRY_POINT_ATTRIBUTE);
	if (entryPoint)
		start.entryPoint = entryPoint;

	const detail = inherited(element, DETAIL_ATTRIBUTE);
	if (detail)
		start.detail = detail;

	return subjectKeyed(start, element);
}

/**
 * The Start as the element makes it, with the subject's own id as the correlation id where the stage keys
 * off one. An element that names no subject falls back to a minted id, which pairs with nothing.
 */
function subjectKeyed(definition: StageStart, element: Element): StageStart {
	const subject = stageSubject(definition.stage);
	if (!subject)
		return definition;

	const subjectId = element.getAttribute(subject.attribute)?.trim();
	return subjectId ? { ...definition, stageCorrelationId: subjectId } : definition;
}

/**
 * Whether the subject this element belongs to has already completed the stage, and so cannot enter it
 * again. Read from the nearest ancestor that names its completed stages, which is where the page that knows
 * the subject puts them.
 */
function alreadyCompleted(element: Element, stage: JourneyStage): boolean {
	return element.closest(`[${COMPLETED_ATTRIBUTE}~="${stage}"]`) !== null;
}

function activate(plugin: ClickAnalyticsLike | null, element: Element): void {
	if (element.hasAttribute(START_ATTRIBUTE)) {
		const start = declaredStart(element);
		if (start && !alreadyCompleted(element, start.stage))
			emit(plugin, element, buildStartSignal(beginStage(start), signalContext(start.stage, element)), `start:${start.stage}`);
		return;
	}

	const stepName = element.getAttribute(STEP_ATTRIBUTE);
	if (!stepName)
		return;

	const requested = requestedStage(element);
	let active = currentStage(requested);
	if (active) {
		active = amendStage(active.stage, {
			variant: declared(element, VARIANT_ATTRIBUTE),
			entryPoint: declared(element, ENTRY_POINT_ATTRIBUTE),
			detail: declared(element, DETAIL_ATTRIBUTE)
		}) ?? active;

		emit(plugin, element, buildStepSignal(stepName, active, signalContext(active.stage, element)), `step:${stepName}`);
		return;
	}

	if (requested)
		emit(plugin, element, buildOrphanStepSignal(stepName, requested, signalContext(requested, element)), `step:${stepName}`);
}

function resolveElement(target: EventTarget | null): Element | null {
	const element = target as Element | null;
	if (!element || typeof element.closest !== 'function')
		return null;
	return element.closest(JOURNEY_SELECTOR);
}

const TEXT_ENTRY_SELECTOR = 'textarea, input:not([type=button]):not([type=submit]):not([type=reset]):not([type=checkbox]):not([type=radio])';

/**
 * Whether the key landed somewhere Enter and Space are text rather than activation. A Start can sit on a
 * field — the publish page puts one on the embed-code textarea — and every space typed in one would
 * otherwise reopen the stage.
 */
function isTextEntry(target: EventTarget | null): boolean {
	const element = target as HTMLElement | null;
	if (!element || typeof element.matches !== 'function')
		return false;

	return element.matches(TEXT_ENTRY_SELECTOR) || element.isContentEditable === true;
}

/**
 * Registers the listener that opens stages and emits their Start and Step events. It runs in the capture
 * phase on `window`, ahead of every application handler and out of reach of `stopPropagation`, so the
 * correlation id exists before the handler fires the request that carries it.
 */
export function installJourneyActivation(getPlugin: () => ClickAnalyticsLike | null): void {
	pluginProvider = getPlugin;

	if (installed)
		return;
	installed = true;

	const handle = (target: EventTarget | null) => {
		try {
			const element = resolveElement(target);
			if (element)
				activate(getPlugin(), element);
		}
		catch (e) {
			warn('journey activation failed:', e);
		}
	};

	window.addEventListener('mousedown', (event: MouseEvent) => {
		if (event.button === 0)
			handle(event.target);
	}, true);

	window.addEventListener('keydown', (event: KeyboardEvent) => {
		if ((event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') && !isTextEntry(event.target))
			handle(event.target);
	}, true);
}
